import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../../contexts/AuthContext';
import { API_CONFIG } from '../../../utils/apiConfig';

const QuickMigration = () => {
  const [migrating, setMigrating] = useState(false);
  const [result, setResult] = useState(null);
  const { currentUser } = useAuth();

  const getAuthToken = async () => {
    if (!currentUser) {
      throw new Error('No authenticated user');
    }
    return await currentUser.getIdToken(true);
  };

  const runQuickMigration = async () => {
    setMigrating(true);
    setResult(null);
    
    try {
      const token = await getAuthToken();
      
      const testResponse = await axios.get(`${API_CONFIG.BASE_URL}/api/test/sold-products`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      
      if (testResponse.data.data.totalSoldProducts === 0) {
        
        // Step 2: Run migration
        const migrationResponse = await axios.post(`${API_CONFIG.BASE_URL}/api/sales-migration/migrate-all-sales`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setResult({
          success: true,
          message: 'Migration completed successfully!',
          details: migrationResponse.data.data
        });
      } else {
        const purifierData = testResponse.data.data.purifierSales;
        setResult({
          success: true,
          message: 'Sold products already exist',
          details: {
            totalSoldProducts: testResponse.data.data.totalSoldProducts,
            purifierSales: purifierData.length,
            totalPurifierSold: testResponse.data.data.totalPurifierSold
          }
        });
      }
      
    } catch (error) {
      setResult({
        success: false,
        message: 'Migration failed',
        error: error.response?.data?.message || error.message
      });
    } finally {
      setMigrating(false);
    }
  };

  return (
    <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
      <h2>🚀 Quick Migration for Purifier Fix</h2>
      <p style={{ color: '#666', marginBottom: '20px' }}>
        This will migrate all cashbills and creditbills data to soldProducts collection to fix the Purifier calculation.
      </p>

      <div style={{ 
        backgroundColor: '#f8f9fa', 
        padding: '20px', 
        borderRadius: '8px', 
        marginBottom: '20px',
        border: '1px solid #dee2e6'
      }}>
        <button
          onClick={runQuickMigration}
          disabled={migrating}
          style={{
            backgroundColor: migrating ? '#6c757d' : '#007bff',
            color: 'white',
            border: 'none',
            padding: '15px 30px',
            borderRadius: '5px',
            cursor: migrating ? 'not-allowed' : 'pointer',
            fontSize: '16px',
            fontWeight: 'bold'
          }}
        >
          {migrating ? '🔄 Migrating...' : '🚀 Run Quick Migration'}
        </button>
      </div>

      {result && (
        <div style={{ 
          backgroundColor: result.success ? '#d4edda' : '#f8d7da', 
          padding: '15px', 
          borderRadius: '5px', 
          marginBottom: '20px',
          border: `1px solid ${result.success ? '#c3e6cb' : '#f5c6cb'}`
        }}>
          <h4 style={{ 
            color: result.success ? '#155724' : '#721c24', 
            margin: '0 0 10px 0' 
          }}>
            {result.success ? '✅ Success!' : '❌ Error'}
          </h4>
          <p style={{ 
            color: result.success ? '#155724' : '#721c24',
            margin: '0 0 10px 0'
          }}>
            {result.message}
          </p>
          
          {result.details && (
            <div style={{ fontSize: '14px', color: result.success ? '#155724' : '#721c24' }}>
              <p><strong>Total Sold Products:</strong> {result.details.totalSoldProducts || result.details.totalMigrated}</p>
              {result.details.purifierSales !== undefined && (
                <>
                  <p><strong>Purifier Sales Found:</strong> {result.details.purifierSales}</p>
                  <p><strong>Total Purifier Sold:</strong> {result.details.totalPurifierSold}</p>
                </>
              )}
              {result.details.cashBillsProcessed && (
                <>
                  <p><strong>Cash Bills Processed:</strong> {result.details.cashBillsProcessed}</p>
                  <p><strong>Credit Bills Processed:</strong> {result.details.creditBillsProcessed}</p>
                </>
              )}
            </div>
          )}
          
          {result.error && (
            <p style={{ color: '#721c24', fontSize: '14px' }}>
              <strong>Error:</strong> {result.error}
            </p>
          )}
        </div>
      )}

      <div style={{ 
        backgroundColor: '#fff3cd', 
        padding: '15px', 
        borderRadius: '5px',
        border: '1px solid #ffeaa7'
      }}>
        <h4 style={{ color: '#856404', margin: '0 0 10px 0' }}>What This Does:</h4>
        <ol style={{ color: '#856404', fontSize: '14px', margin: '0' }}>
          <li>Checks if soldProducts collection has data</li>
          <li>If empty, migrates all cashbills and creditbills data</li>
          <li>Fixes the Purifier "Total Sold" calculation</li>
          <li>Shows detailed results of the migration</li>
        </ol>
      </div>
    </div>
  );
};

export default QuickMigration;
